import React, { useState, useCallback } from "react";
import {
  AppLayout as CloudscapeAppLayout,
  TopNavigation,
} from "@cloudscape-design/components";
import AnnotationContext from "@cloudscape-design/components/annotation-context";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { AIAssistantWidget } from "@amzn/wwasft-components";
import "@amzn/wwasft-components/styles.css";
import { CustomNavigationActions } from "../Header/CustomNavigationActions";
import { FinergyFloatingMenu } from "../Header/FinergyFloatingMenu";
import Breadcrumb from "../common/Breadcrumb";
import { useBreadcrumbs } from "src/hooks/useBreadcrumbs";
import GradientEffects from "../common/GradientBackground/GradientEffects";
import { Notification } from "../common/Notification";
import FinSpaceLogo from "../../images/FinSpaceLogo.svg";
import { getAIAssistantConfig } from "src/config/aiAssistantConfig";
import { useFeatureFlagStore } from "src/store/feature-flag-store";
import { FeatureAccess } from "src/hooks/useFeatureAccessControls";
import { useUserAlias } from "src/hooks";
import { useTheme } from "src/providers";
import { Mode } from "@cloudscape-design/global-styles";
import { useTutorial, TutorialBanner } from "src/tutorials";
import "./app-layout.scss";
import "./app-layout-navigation.scss";

/** i18n strings for AnnotationContext - Modern Gen AI style */
const annotationI18nStrings = {
  nextButtonText: 'Continue',
  previousButtonText: 'Back',
  finishButtonText: 'Get Started!',
  labelDismissAnnotation: 'Close',
  labelHotspot: (_openState: boolean, stepIndex: number, totalStepCount: number) =>
    `Step ${stepIndex + 1} of ${totalStepCount}`,
  stepCounterText: (stepIndex: number, totalStepCount: number) =>
    `Step ${stepIndex + 1} of ${totalStepCount}`,
  taskTitle: (_taskIndex: number, taskTitle: string) => taskTitle,
};

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { alias } = useUserAlias();
  const { mode } = useTheme();
  const { hasFeatureAccess } = useFeatureFlagStore();
  const isAIAssistantEnabled = hasFeatureAccess(FeatureAccess.AI_ASSISTANT);
  
  const { 
    currentTutorial, 
    setStep, 
    startTutorial, 
    exitTutorial, 
    completeTutorial 
  } = useTutorial();

  const handleHeaderClick = (event: CustomEvent) => {
    event.preventDefault();
    navigate("/dashboard");
  };

  const getAuthToken = useCallback(async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }, []);

  const handleChatClose = useCallback(() => setIsChatOpen(false), []);

  const handleStepChange = useCallback(({ detail }: { detail: { step: number } }) => {
    setStep(detail.step);
  }, [setStep]);

  const handleStartTutorial = useCallback(({ detail }: { detail: { tutorial: any } }) => {
    startTutorial(detail.tutorial);
  }, [startTutorial]);

  const handleExitTutorial = useCallback(() => {
    exitTutorial();
  }, [exitTutorial]);

  const handleFinish = useCallback(() => {
    completeTutorial();
    setIsChatOpen(true); // Open chat after completing tour
  }, [completeTutorial]);

  return (
    <AnnotationContext
      currentTutorial={currentTutorial}
      onStepChange={handleStepChange}
      onStartTutorial={handleStartTutorial}
      onExitTutorial={handleExitTutorial}
      onFinish={handleFinish}
      i18nStrings={annotationI18nStrings}
    >
      <Notification />
      {isAIAssistantEnabled && <TutorialBanner />}
      <div id="top-navigation">
        <FinergyFloatingMenu />
        <TopNavigation
          identity={{
            href: "/dashboard",
            logo: {
              src: FinSpaceLogo,
              alt: "FinSpace Logo"
            },
            onFollow: handleHeaderClick,
          }}
        />
        <CustomNavigationActions onAskAI={isAIAssistantEnabled ? () => setIsChatOpen(true) : undefined} />
      </div>

      <CloudscapeAppLayout
        navigationHide
        maxContentWidth={Number.MAX_VALUE}
        toolsHide
        disableContentPaddings
        {...(breadcrumbs.length > 1 && { breadcrumbs: <Breadcrumb /> })}
        content={
          <>
            <GradientEffects />
            <div className="app-content-container">
              {children}
            </div>
          </>
        }
      />

      {isAIAssistantEnabled && (
        <AIAssistantWidget
          config={{
            ...getAIAssistantConfig(),
            getAuthToken,
            userAlias: alias,
            visibleToolbarActions: ['new-chat'],
          }}
          isOpen={isChatOpen}
          onClose={handleChatClose}
          theme={mode === Mode.Light ? 'light' : 'dark'}
        />
      )}
    </AnnotationContext>
  );
};

export default AppLayout;
